import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Download, RefreshCw, Copy, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/diagnostics/admin/reports`;

function displayDate(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default function AdminDiagnostics() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await axios.get(API, { params });
      setItems(data.items || []);
      setError("");
    } catch (e) {
      setError(
        e?.response?.status === 401 || e?.response?.status === 403
          ? "You do not have access to technical reports."
          : "Technical reports could not be loaded."
      );
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function openReport(id) {
    try {
      const { data } = await axios.get(`${API}/${encodeURIComponent(id)}`);
      setSelected(data);
      setCopied(false);
      setError("");
    } catch {
      setError("Report details could not be loaded.");
    }
  }

  async function changeStatus(status) {
    if (!selected) return;
    try {
      await axios.patch(`${API}/${encodeURIComponent(selected.id)}`, { status });
      setSelected({ ...selected, status });
      await refresh();
    } catch {
      setError("The report status could not be changed.");
    }
  }

  async function copyReport() {
    if (!selected) return;
    const content = [
      "Technical report: " + selected.id,
      "Type: " + (selected.kind || ""),
      "Page: " + (selected.route || ""),
      "Date: " + (selected.created_at || ""),
      "Build: " + (selected.build_sha || ""),
      "Status: " + (selected.status || ""),
      "Message: " + (selected.message || ""),
      "Stack trace:", selected.stack || "Unavailable",
      "Recent requests and navigation:", JSON.stringify({ breadcrumbs: selected.breadcrumbs || [], network_errors: selected.network_errors || [] }, null, 2),
      "Environment:", JSON.stringify(selected.environment || {}, null, 2),
    ].join("\n");
    try { await navigator.clipboard.writeText(content); setCopied(true); }
    catch { setError("Clipboard access was blocked. Use the report ZIP download instead."); }
  }

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("report");
    if (id) openReport(id);
    // The report ID is an optional deep-link from the administrator work queue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function downloadZip() {
    if (!selected) return;
    try {
      const { data } = await axios.get(
        `${API}/${encodeURIComponent(selected.id)}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Yasharal-Crash-Report-${selected.id}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError("The ZIP could not be downloaded.");
    }
  }

  return (
    <main className="mx-auto w-full max-w-7xl px-4 py-10 text-slate-900">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-royal-blue">Admin</p>
          <h1 className="text-3xl font-bold">Technical Diagnostics</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Select a report using the View details control below to inspect
            its stack, recent navigation, failed request paths, environment, and downloadable ZIP.
          </p>
        </div>
        <Link to="/nationals/admin" className="inline-flex items-center gap-2 rounded border px-4 py-2">
          <ArrowLeft className="h-4 w-4" /> Admin
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm font-semibold" htmlFor="diagnostic-status">Status</label>
        <select
          id="diagnostic-status"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded border bg-white px-3 py-2"
        >
          <option value="all">All</option>
          <option value="new">New</option>
          <option value="investigating">Investigating</option>
          <option value="resolved">Resolved</option>
          <option value="ignored">Ignored</option>
        </select>
        <button type="button" onClick={refresh} className="inline-flex items-center gap-2 rounded border px-3 py-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {busy && <p>Loading technical reports…</p>}
      {error && <p role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-red-800">{error}</p>}

      <div className="overflow-x-auto rounded border bg-white">
        <table className="w-full border-collapse text-left text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="p-3">Report / action</th>
              <th className="p-3">When</th>
              <th className="p-3">Type</th>
              <th className="p-3">Page</th>
              <th className="p-3">Status</th>
              <th className="p-3">Occurrences</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t align-top">
                <td className="p-3">
                  <button type="button" className="inline-flex items-center gap-2 font-semibold text-royal-blue underline" aria-expanded={selected?.id === item.id} onClick={() => selected?.id === item.id ? setSelected(null) : openReport(item.id)}>
                    <ChevronDown className="h-4 w-4" /> {selected?.id === item.id ? "Hide details" : "View details"}
                  </button>
                  <div className="mt-1 text-xs text-slate-600 break-all">{item.id}</div>
                </td>
                <td className="p-3 whitespace-nowrap">{displayDate(item.created_at)}</td>
                <td className="p-3">{item.kind}</td>
                <td className="p-3">{item.route}</td>
                <td className="p-3">{item.status}</td>
                <td className="p-3">{item.occurrence_count || 1}</td>
              </tr>
            ))}
            {!busy && items.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-slate-500">No reports match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <section className="mt-8 rounded-xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold">{selected.id}</h2>
              <p className="mt-1 text-sm text-slate-600">{displayDate(selected.created_at)} · {selected.route}</p>
            </div>
            <div className="flex flex-wrap gap-2"><button type="button" onClick={copyReport} className="inline-flex items-center gap-2 rounded border border-royal-blue px-4 py-2 font-semibold text-royal-blue"><Copy className="h-4 w-4" /> {copied ? "Copied report" : "Copy full report"}</button><button type="button" onClick={downloadZip} className="inline-flex items-center gap-2 rounded bg-royal-blue px-4 py-2 font-semibold text-white">
              <Download className="h-4 w-4" /> Download ZIP
            </button></div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <div className="rounded border p-3"><span className="font-semibold">Type:</span> {selected.kind}</div>
            <div className="rounded border p-3"><span className="font-semibold">Status:</span> {selected.status}</div>
            <div className="rounded border p-3"><span className="font-semibold">Release:</span> {selected.release || "unknown"}</div>
            <div className="rounded border p-3"><span className="font-semibold">Build:</span> {selected.build_sha || "unknown"}</div>
          </div>

          <h3 className="mt-6 font-semibold">Message</h3>
          <p className="mt-2 rounded bg-slate-50 p-3">{selected.message}</p>

          <h3 className="mt-6 font-semibold">Stack trace</h3>
          <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-slate-950 p-4 text-xs text-white">
            {selected.stack || "No JavaScript stack was available."}
          </pre>

          <details className="mt-5 rounded border p-3">
            <summary className="cursor-pointer font-semibold">Recent navigation and failed requests</summary>
            <pre className="mt-3 overflow-auto whitespace-pre-wrap text-xs">
              {JSON.stringify({ breadcrumbs: selected.breadcrumbs || [], network_errors: selected.network_errors || [] }, null, 2)}
            </pre>
          </details>

          <details className="mt-3 rounded border p-3">
            <summary className="cursor-pointer font-semibold">Environment</summary>
            <pre className="mt-3 overflow-auto whitespace-pre-wrap text-xs">
              {JSON.stringify(selected.environment || {}, null, 2)}
            </pre>
          </details>

          <div className="mt-5 flex flex-wrap gap-2">
            {["new", "investigating", "resolved", "ignored"].map((status) => (
              <button
                type="button"
                key={status}
                disabled={selected.status === status}
                onClick={() => changeStatus(status)}
                className="rounded border px-3 py-2 capitalize disabled:bg-slate-100 disabled:text-slate-400"
              >
                {status}
              </button>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
